import React, { useState } from 'react';
import { usePageMetadata } from '../hooks/usePageMetadata';
import { Breadcrumb } from '../components/Breadcrumb';
import { WhatNextSection } from '../components/WhatNextSection';
import { ParticlesBackground } from '../components/effects/ParticlesBackground';
import { AnimatedBlob } from '../components/effects/AnimatedBlob';
import { RevealOnScroll } from '../components/effects/RevealOnScroll';
import { GlassCard } from '../components/ui/GlassCard';
import { MessageCircle, RefreshCw, ExternalLink, Loader2, AlertTriangle } from 'lucide-react';

export const ZapytajChataAiPage: React.FC = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const iframeUrl = "https://nodessc.com/form/generator-promptow";

    usePageMetadata({
        title: "ZapytajChata AI | Mini-Chatbot FAQ dla Twojej Strony",
        description: "Wdróż bota Q&A na swojej stronie w 5 minut. ZapytajChata AI to proste narzędzie do odpowiadania na pytania klientów na podstawie Twojej bazy wiedzy.",
    });

    const reloadFrame = () => {
        setIsLoading(true);
        setHasError(false);
        const iframe = document.getElementById('contentFrame') as HTMLIFrameElement;
        if (iframe) {
            iframe.src += '';
        }
    };

    return (
        <div className="flex flex-col min-h-[calc(100vh-64px)]">
            {/* Hero Header */}
            <section className="relative overflow-hidden py-8">
                <ParticlesBackground variant="minimal" />
                <AnimatedBlob variant="mixed" />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-bkg-light/80 to-bkg-light dark:via-bkg-dark/80 dark:to-bkg-dark pointer-events-none" />

                <div className="container mx-auto px-4 relative z-10">
                    <RevealOnScroll>
                        <GlassCard variant="medium" className="flex flex-col sm:flex-row justify-between items-center gap-4">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-light to-secondary-light dark:from-primary-dark dark:to-secondary-dark flex items-center justify-center">
                                    <MessageCircle className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <h1 className="text-2xl font-bold bg-gradient-to-r from-primary-light to-secondary-light dark:from-primary-dark dark:to-secondary-dark bg-clip-text text-transparent">
                                        ZapytajChata AI
                                    </h1>
                                    <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                                        Generator promptów AI
                                    </p>
                                </div>
                            </div>
                            <nav className="flex gap-2">
                                <button
                                    onClick={reloadFrame}
                                    className="px-4 py-2 text-sm font-semibold rounded-xl bg-white/10 dark:bg-white/5 border border-white/20 dark:border-white/10 text-primary-light dark:text-primary-dark hover:bg-white/20 dark:hover:bg-white/10 transition-all flex items-center gap-2"
                                >
                                    <RefreshCw className="w-4 h-4" />
                                    Odśwież
                                </button>
                                <a
                                    href={iframeUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="px-4 py-2 text-sm font-semibold rounded-xl bg-gradient-to-r from-primary-light to-secondary-light dark:from-primary-dark dark:to-secondary-dark text-white hover:shadow-lg hover:shadow-primary-light/30 dark:hover:shadow-primary-dark/30 transition-all flex items-center gap-2"
                                >
                                    <ExternalLink className="w-4 h-4" />
                                    Pełny ekran
                                </a>
                            </nav>
                        </GlassCard>
                    </RevealOnScroll>
                </div>
            </section>

            <div className="px-4 pb-4">
                <Breadcrumb />
            </div>

            {/* Main Content */}
            <main className="flex-grow relative mx-4 mb-4">
                <GlassCard variant="subtle" className="h-full min-h-[60vh] p-0 overflow-hidden relative">
                    {isLoading && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-bkg-light/80 dark:bg-bkg-dark/80 backdrop-blur-sm z-10">
                            <Loader2 className="w-12 h-12 text-primary-light dark:text-primary-dark animate-spin" />
                            <p className="mt-4 text-text-secondary-light dark:text-text-secondary-dark">Ładowanie generatora...</p>
                        </div>
                    )}
                    {hasError && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-bkg-light/80 dark:bg-bkg-dark/80 backdrop-blur-sm z-10 p-4">
                            <GlassCard variant="medium" className="bg-red-500/10 border-red-500/30 text-center max-w-md">
                                <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                                <h2 className="text-xl font-bold text-red-500 dark:text-red-400 mb-2">
                                    Nie można załadować generatora
                                </h2>
                                <p className="text-text-secondary-light dark:text-text-secondary-dark mb-4">
                                    Wystąpił problem z załadowaniem narzędzia.
                                </p>
                                <button
                                    onClick={reloadFrame}
                                    className="px-6 py-2 font-semibold text-white bg-gradient-to-r from-primary-light to-secondary-light dark:from-primary-dark dark:to-secondary-dark rounded-xl hover:shadow-lg transition-all"
                                >
                                    Spróbuj ponownie
                                </button>
                            </GlassCard>
                        </div>
                    )}
                    <iframe
                        id="contentFrame"
                        src={iframeUrl}
                        title="Generator Promptów AI"
                        className="w-full h-full min-h-[60vh] border-0"
                        onLoad={() => setIsLoading(false)}
                        onError={() => { setIsLoading(false); setHasError(true); }}
                        sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                    />
                </GlassCard>
            </main>

            <WhatNextSection diyLink="/rozwiazania/zapytaj-chata-ai" />
        </div>
    );
};
